
const TRUSTED_CDNS = [
    'static.sketchfab.com',
    'media.sketchfab.com'
];

/**
 * Helper: Extracts the apex domain (SLD.TLD) from a hostname.
 * e.g. "api.dev.artetra.io" -> "artetra.io"
 * Note: A simple slice(-2) usually suffices for .com, .io, .net.
 * For complex TLDs (co.uk), this might need a more robust library, 
 * but for this project's known domains, slice(-2) is sufficient and performant.
 */
const getApexDomain = (hostname: string) => {
    const parts = hostname.split('.');
    if (parts.length < 2) return hostname;
    return parts.slice(-2).join('.');
};

const matchesTrustedOrigin = (candidate: URL, trustedOrigins: readonly string[]): boolean => (
    trustedOrigins.some((value) => {
        try {
            const trusted = new URL(value);
            return trusted.protocol === 'https:'
                && !trusted.username
                && !trusted.password
                && !trusted.search
                && !trusted.hash
                && trusted.pathname === '/'
                && candidate.origin === trusted.origin;
        } catch (_error) {
            return false;
        }
    })
);

const encodeOwnedObjectHash = (value: string, candidate: URL): string => {
    if (!candidate.hash) return value;
    if (/\.(?:avif|gif|jpe?g|png|webp)$/i.test(candidate.pathname)) return value;
    const hashIndex = value.indexOf('#');
    if (hashIndex < 0) return value;
    const encoded = `${value.slice(0, hashIndex)}%23${value.slice(hashIndex + 1).replace(/#/g, '%23')}`;
    try {
        return /\.(?:avif|gif|jpe?g|png|webp)$/i.test(new URL(encoded).pathname)
            ? encoded
            : value;
    } catch (_error) {
        return value;
    }
};

/**
 * Generates a proxied URL for external images to avoid CORS and Mixed Content issues.
 * 
 * Policy:
 * 1. Empty URL -> Return empty string
 * 2. Local Assets (data:, blob:, /, ./, ../) -> Return as-is
 * 3. Owned Assets (shares apex domain with baseAPI) -> Return as-is
 * 4. Trusted Global CDNs (Sketchfab) -> Return as-is
 * 5. Everything else -> Proxy through backend
 * 
 * @param url The image URL to process
 * @param baseAPI The base URL of the backend API (e.g. process.env.REACT_APP_BASE_API)
 */
export const getProxiedImageUrl = (
    url: string,
    baseAPI: string,
    trustedOrigins: readonly string[] = [],
): string => {
    if (!url) return '';

    // 1. Local / In-memory assets
    if (
        url.startsWith('data:') ||
        url.startsWith('blob:') ||
        url.startsWith('/') ||
        url.startsWith('./') ||
        url.startsWith('../')
    ) {
        return url;
    }

    // 2. Early double-proxy guard (micro-optimization)
    if (url.includes('/proxy/image')) {
        return url;
    }

    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();

        if (matchesTrustedOrigin(urlObj, trustedOrigins)) {
            return encodeOwnedObjectHash(url, urlObj);
        }

        // 3 & 4. Check for Trusted Domains (Owned + Whitelisted)
        if (baseAPI && baseAPI.startsWith('http')) {
            try {
                const apiHostname = new URL(baseAPI).hostname.toLowerCase();
                const apiApex = getApexDomain(apiHostname);

                // If the image is on the same apex domain (e.g. cdn.artetra.io vs api.artetra.io)
                // we treat it as internal/owned and do NOT proxy.
                const matchesDomain = hostname === apiApex || hostname.endsWith('.' + apiApex);
                if (matchesDomain) {
                    return encodeOwnedObjectHash(url, urlObj);
                }
            } catch (ignore) {
                // If baseAPI is malformed, we proceed to other checks
            }
        }

        // 5. Trusted Global CDNs
        if (TRUSTED_CDNS.some(cdn => hostname.endsWith(cdn))) {
            return url;
        }

    } catch (_error) {
        console.warn('Invalid image media reference');
        return '';
    }

    // 6. Proxy everything else (catch-all for untrusted external content)
    if (url.startsWith('http://') || url.startsWith('https://')) {
        const cleanBaseAPI = baseAPI ? baseAPI.replace(/\/+$/, '') : '';
        return `${cleanBaseAPI}/proxy/image?url=${encodeURIComponent(url)}`;
    }

    // Fallback for unknown schemes
    return url;
};

export const getProxiedSoundUrl = (
    url: string,
    baseAPI: string,
    trustedOrigins: readonly string[] = [],
): string => {
    if (!url) return '';
    const maxReferenceLength = 8192;
    const maxProxyDepth = 3;
    const internalReferenceBase = 'https://artetra.invalid';
    let current = url.trim();
    if (!current || current.length > maxReferenceLength) return '';
    if (
        current.startsWith('data:') ||
        current.startsWith('blob:')
    ) {
        return current;
    }

    for (let depth = 0; depth <= maxProxyDepth; depth += 1) {
        let parsed: URL;
        const isInternal = current.startsWith('/');
        if (
            current.startsWith('//') ||
            current.startsWith('./') ||
            current.startsWith('../') ||
            current.includes('\\')
        ) {
            return '';
        }
        try {
            parsed = isInternal
                ? new URL(current, internalReferenceBase)
                : new URL(current);
        } catch (_error) {
            return '';
        }
        if (!['http:', 'https:'].includes(parsed.protocol)) return '';
        if (parsed.username || parsed.password) return '';
        if (parsed.pathname.toLowerCase().endsWith('/proxy/sound')) {
            if (depth === maxProxyDepth) return '';
            const target = parsed.searchParams.get('url')?.trim();
            if (!target || target.length > maxReferenceLength) return '';
            current = target;
            continue;
        }

        if (isInternal) {
            const rawPath = current.split(/[?#]/, 1)[0];
            let decodedPath = '';
            try {
                decodedPath = decodeURIComponent(rawPath);
            } catch (_error) {
                return '';
            }
            if (
                parsed.search ||
                parsed.hash ||
                decodedPath.split('/').some((segment) => segment === '.' || segment === '..')
            ) {
                return '';
            }
            return current;
        }

        const hostname = parsed.hostname.toLowerCase().replace(/^\[|\]$/g, '');
        if (
            hostname === 'localhost' ||
            hostname.endsWith('.localhost') ||
            hostname === '::1' ||
            /^127\./.test(hostname)
        ) {
            return '';
        }
        if (matchesTrustedOrigin(parsed, trustedOrigins)) {
            return current;
        }

        const cleanBaseAPI = baseAPI ? baseAPI.replace(/\/+$/, '') : '';
        if (!cleanBaseAPI) return '';
        try {
            const api = new URL(cleanBaseAPI);
            if (!['http:', 'https:'].includes(api.protocol) || api.username || api.password) return '';
        } catch (_error) {
            return '';
        }
        return `${cleanBaseAPI}/proxy/sound?url=${encodeURIComponent(current)}`;
    }

    return '';
};
